<template>
  <div
    class="redacao-sportv-container"
    :class="[`position-${posicaoEnquete}`, { 'fullscreen-mode': isFullScreen }]"
    :key="item.id"
  >
    <!-- Title Bar (separate from options) -->
    <div v-if="!hideTitle" class="title-bar">
      <span class="title-text">{{ item.dsc_titulo_enquete }}</span>
    </div>

    <!-- Main Content Area (options only) -->
    <div class="main-content">
      <!-- Watermark text -->
      <div class="watermark-text">
        <span v-for="n in watermarkRepeatCount" :key="n">reda</span>
      </div>

      <!-- Enquete Image (left side) -->
      <div v-if="hasEnqueteImage" class="enquete-image-section">
        <img
          :src="item.url_imagem_enquete"
          :alt="item.dsc_titulo_enquete"
          class="enquete-image"
          @error="handleEnqueteImageError"
        />
      </div>

      <!-- Options Grid -->
      <div class="options-grid" :class="{ 'has-images': hasOptionsWithImages, 'has-enquete-image': hasEnqueteImage, 'align-start': hasEnqueteImage && hasMultipleOptions }">
        <div
          v-for="opcao in limitedOpcoes"
          :key="opcao.id"
          class="option-item"
        >
          <!-- Image (if available and not broken) -->
          <div v-if="opcao.url_imagem && !brokenImages[opcao.id]" class="option-image">
            <img
              :src="opcao.url_imagem"
              :alt="opcao.dsc_titulo"
              @error="handleImageError(opcao.id)"
            />
          </div>

          <!-- Right section: Label on top, percentage + bar below -->
          <div class="option-content">
            <!-- Label -->
            <span
              v-if="!hideOptionTitle"
              class="option-label"
              :class="getOptionLabelClass()"
              :style="optionMaxWidth ? { maxWidth: optionMaxWidth + 'px' } : {}"
            >{{ opcao.dsc_titulo }}</span>

            <!-- Progress Bar + Percentage row -->
            <div v-if="item.flg_exibir_resultado" class="percentage-row">
              <div class="progress-bar-container">
                <div class="progress-bar-fill" :style="getProgressBarStyle(opcao)"></div>
              </div>
              <span class="percentage-value">
                {{ animatedPercentages[opcao.id] || 0 }}%
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CardTarjaEnqueteRedacaoSportv',
  props: {
    item: Object,
    posicaoEnquete: String,
    isFullScreen: Boolean,
    hideTitle: Boolean,
    hideOptionTitle: Boolean,
  },
  data() {
    return {
      animatedPercentages: {},
      animationDuration: 1200,
      optionColors: {},
      brokenImages: {},
      brokenEnqueteImage: false,
      animationFrameIds: {},
    };
  },
  created() {
    for (const option of (this.item?.enquete_opcoes || []).slice(0, 8)) {
      if (option.dsc_cor_barra) {
        this.optionColors[option.id] = option.dsc_cor_barra;
      }
    }
  },
  computed: {
    limitedOpcoes() {
      return (this.item?.enquete_opcoes || []).slice(0, 8);
    },
    hasOptionsWithImages() {
      return this.limitedOpcoes.some(
        opcao => opcao.url_imagem && !this.brokenImages[opcao.id]
      );
    },
    hasEnqueteImage() {
      return this.item?.url_imagem_enquete && !this.brokenEnqueteImage;
    },
    hasMultipleOptions() {
      return this.limitedOpcoes.length > 3;
    },
    watermarkRepeatCount() {
      return 3;
    },
    optionCount() {
      return this.limitedOpcoes.length;
    },
    longTextCount() {
      return this.limitedOpcoes.filter(opcao => (opcao.dsc_titulo || '').length > 20).length;
    },
    maxTextLength() {
      return Math.max(...this.limitedOpcoes.map(opcao => (opcao.dsc_titulo || '').length), 0);
    },
    layoutStrategy() {
      const count = this.optionCount;
      const longCount = this.longTextCount;

      if (count >= 7) {
        if (longCount > 0) {
          return 'compact';
        }
        return 'extra-small';
      }

      if (count >= 5) {
        return longCount > 0 ? 'extra-small' : 'normal';
      }

      if (longCount >= 1) {
        return 'small';
      }

      return 'normal';
    },
    optionMaxWidth() {
      const maxLength = this.maxTextLength;
      const strategy = this.layoutStrategy;

      if (strategy === 'normal') {
        return null;
      }

      const charWidths = {
        'small': 11,
        'extra-small': 9,
        'compact': 7
      };

      const charWidth = charWidths[strategy] || 11;
      const calculatedWidth = Math.ceil(maxLength * 0.8) * charWidth + 30;

      const bounds = {
        'small': { min: 140, max: 280 },
        'extra-small': { min: 120, max: 220 },
        'compact': { min: 100, max: 180 }
      };

      const { min, max } = bounds[strategy] || { min: 120, max: 250 };
      return Math.max(min, Math.min(calculatedWidth, max));
    },
  },
  watch: {
    'item.flg_exibir_resultado': {
      handler(newVal) {
        if (newVal) {
          this.limitedOpcoes.forEach((opcao) => {
            this.animateValue(opcao.id, 0, opcao.resultado || 0);
          });
        } else {
          this.animatedPercentages = {};
        }
      },
      immediate: true,
    },
    'item.enquete_opcoes': {
      handler(newOpcoes, oldOpcoes) {
        if (newOpcoes) {
          const limitedNewOpcoes = newOpcoes.slice(0, 8);
          const limitedOldOpcoes = oldOpcoes?.slice(0, 8);

          limitedNewOpcoes.forEach((opcao) => {
            const oldOpcao = limitedOldOpcoes?.find(o => o.id === opcao.id);
            if (oldOpcao && oldOpcao.url_imagem !== opcao.url_imagem) {
              this.$set(this.brokenImages, opcao.id, false);
            }
          });

          if (this.item.flg_exibir_resultado) {
            limitedNewOpcoes.forEach((opcao) => {
              const currentValue = this.animatedPercentages[opcao.id] || 0;
              if (currentValue !== opcao.resultado) {
                this.animateValue(opcao.id, currentValue, opcao.resultado || 0);
              }
              if (opcao.dsc_cor_barra) {
                this.$set(this.optionColors, opcao.id, opcao.dsc_cor_barra);
              } else {
                this.$delete(this.optionColors, opcao.id);
              }
            });
          }
        }
      },
      deep: true,
    },
    'item.url_imagem_enquete': {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.brokenEnqueteImage = false;
        }
      },
    },
  },
  beforeDestroy() {
    Object.values(this.animationFrameIds).forEach(id => cancelAnimationFrame(id));
  },
  methods: {
    animateValue(key, fromValue, toValue) {
      if (this.animationFrameIds[key]) {
        cancelAnimationFrame(this.animationFrameIds[key]);
      }
      const startTime = Date.now();
      const difference = toValue - fromValue;

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / this.animationDuration, 1);
        const easeOutCubic = 1 - Math.pow(1 - progress, 3);

        this.$set(
          this.animatedPercentages,
          key,
          Math.round(fromValue + difference * easeOutCubic)
        );

        if (progress < 1) {
          this.animationFrameIds[key] = requestAnimationFrame(animate);
        } else {
          delete this.animationFrameIds[key];
        }
      };

      this.animationFrameIds[key] = requestAnimationFrame(animate);
    },
    getProgressBarStyle(opcao) {
      const percentage = this.animatedPercentages[opcao.id] || 0;
      const style = { width: percentage + '%' };
      if (this.optionColors[opcao.id]) {
        style.backgroundColor = this.optionColors[opcao.id];
      }
      return style;
    },
    handleImageError(opcaoId) {
      this.$set(this.brokenImages, opcaoId, true);
    },
    handleEnqueteImageError() {
      this.brokenEnqueteImage = true;
    },
    getOptionLabelClass() {
      const strategy = this.layoutStrategy;

      if (strategy === 'compact') {
        return 'option-label--compact';
      }

      if (strategy === 'extra-small') {
        return 'option-label--extra-small';
      }

      if (strategy === 'small') {
        return 'option-label--small';
      }

      return '';
    },
  },
};
</script>

<style lang="scss" scoped>
@font-face {
  font-family: "IndustryMedium";
  font-weight: 400;
  src: url("@/assets/fonts/RedacaoSportv/Industry-Medium.ttf") format("truetype");
}

@font-face {
  font-family: "IndustryDemi";
  font-weight: 600;
  src: url("@/assets/fonts/RedacaoSportv/Industry-Demi.ttf") format("truetype");
}

@font-face {
  font-family: "IndustryBlack";
  font-weight: 900;
  src: url("@/assets/fonts/RedacaoSportv/Industry-Black.ttf") format("truetype");
}

.redacao-sportv-container {
  --background-gradient: linear-gradient(to right, #0148ff, #0148ff);
  --title-bar-background-gradient: linear-gradient(to right, #000176, #000176);
  --title-text-color: #ffffff;
  --progress-bar-bg: #5f89f9;
  --progress-bar-fill: #000176;
  --option-text-color: #ffffff;
  --percentage-text-color: #ffffff;

  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  width: fit-content;
  min-width: 1050px;
  height: 194px;
  margin-bottom: 85px;

  &.position-esquerda {
    justify-self: flex-start;
    margin-left: 50px;
  }

  &.position-centro {
    justify-self: center;
    margin-left: auto;
    margin-right: auto;
  }

  &.position-direita {
    justify-self: flex-end;
    margin-right: 50px;
  }

  &.fullscreen-mode {
    max-width: 1200px;

    .title-bar {
      padding: 14px 40px;

      .title-text {
        font-size: 32px;
      }
    }

    .options-grid {
      padding: 30px 40px;
    }

    .percentage-value {
      font-size: 42px;
    }

    .option-label {
      font-size: 28px;

      &--small {
        font-size: 20px;
      }

      &--extra-small {
        font-size: 18px;
      }

      &--compact {
        font-size: 16px;
      }
    }

    .option-image img {
      width: 64px;
      height: 64px;
    }

    .progress-bar-container {
      width: 120px;
      height: 28px;
    }

    .enquete-image-section {
      padding: 15px 10px 15px 25px;

      .enquete-image {
        width: 120px;
        height: 120px;
      }
    }
  }
}

.main-content {
  background: var(--background-gradient);
  overflow: hidden;
  position: relative;
  flex: 1;
  display: flex;
  min-width: 1050px;
  flex-direction: row;
  align-items: center;
}

.watermark-text {
  position: absolute;
  right: -26px;
  top: 78%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 5px;
  pointer-events: none;
  z-index: 0;

  span {
    font-family: 'IndustryBlack', sans-serif;
    font-weight: 900;
    font-size: 123px;
    color: transparent;
    -webkit-text-stroke: 1px #fab300;
    white-space: nowrap;
    line-height: 0.9;
    letter-spacing: 2px;
  }
}

.enquete-image-section {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 5px 10px 15px;
  flex-shrink: 0;
  position: relative;
  z-index: 1;

  .enquete-image {
    width: 90px;
    height: 90px;
    object-fit: contain;
  }
}

.title-bar {
  background: var(--title-bar-background-gradient);
  padding: 5px 30px;
  z-index: 1;

  .title-text {
    display: block;
    color: var(--title-text-color);
    font-family: 'IndustryBlack', sans-serif;
    font-weight: 900;
    font-size: 25px;
    text-align: center;
    text-transform: uppercase;
  }
}

.options-grid {
  display: grid;
  grid-template-columns: repeat(4, auto);
  justify-content: center;
  align-content: center;
  align-items: flex-end;
  gap: 10px 20px;
  padding: 15px 240px 15px 15px;
  width: 100%;
  position: relative;
  z-index: 1;

  &.has-enquete-image {
    padding-left: 0;
  }

  &.align-start {
    justify-content: start;
  }
}

.option-item {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 8px;
  padding: 5px 10px;
}

.option-image {
  flex-shrink: 0;
  align-self: flex-end;
  margin-bottom: 2px;

  img {
    width: 36px;
    height: 36px;
    object-fit: contain;
    display: block;
  }
}

.option-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
}

.option-label {
  font-family: 'IndustryMedium', sans-serif;
  font-weight: 400;
  font-size: 25px;
  color: var(--option-text-color);
  white-space: nowrap;
  line-height: 1.1;
  text-transform: uppercase;

  &--small {
    font-size: 18px;
    white-space: normal;
    word-wrap: break-word;
    overflow-wrap: break-word;
    line-height: 1.2;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &--extra-small {
    font-size: 15px;
    white-space: normal;
    word-wrap: break-word;
    overflow-wrap: break-word;
    line-height: 1.2;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &--compact {
    font-size: 13px;
    white-space: normal;
    word-wrap: break-word;
    overflow-wrap: break-word;
    line-height: 1.2;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
}

.percentage-row {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.percentage-value {
  font-family: 'IndustryMedium', sans-serif;
  font-weight: 400;
  font-size: 25px;
  color: var(--percentage-text-color);
  line-height: 1;
  margin-left: 8px;
}

.progress-bar-container {
  min-width: 80px;
  width: 80px;
  height: 20px;
  background-color: var(--progress-bar-bg);
  overflow: hidden;
  flex-shrink: 0;
}

.progress-bar-fill {
  height: 100%;
  background-color: var(--progress-bar-fill);
  transition: width 0.3s ease;
}
</style>
